/**
 * useQuery hook for counting stories across a set of folder IDs.
 *
 * Replaces `storyListEffects.getFoldersStoryCount` (compose-app). Fires
 * `getStoryCountInFolders` via `postQuery` and returns the aggregate count.
 *
 * Auth note (§1): non-admin reachable → `postQuery`.
 *
 * Pass `null` to disable (§9). Pass an empty `folderIds` array to skip the
 * request entirely (enabled is false when the array is empty).
 */

import { useQuery } from '@tanstack/react-query';

import { postQuery } from '../../../effects/publications/postQuery';
import { getStoryCountInFolders } from '../../../graphQL/queries/listQueries';
import { useToast } from '../../Toast/context/useToast.context';
import { storyListQueryKeys, FoldersStoryCountKeyParams } from './storyListQueryKeys';

export type UseFoldersStoryCountParams = FoldersStoryCountKeyParams | null;

export function useFoldersStoryCount(params: UseFoldersStoryCountParams, errorMessage: string) {
  const { toggleToast } = useToast();

  return useQuery<number, Error>({
    queryKey: params
      ? storyListQueryKeys.foldersStoryCount(params)
      : ([...storyListQueryKeys.all, 'foldersStoryCount', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const response = await postQuery(getStoryCountInFolders, { folderIds: params.folderIds });
      return response.count.aggregate.count as number;
    },
    enabled: params !== null && params.folderIds.length > 0,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
